/**
 * Custom Hook: AI Studio Preferences
 *
 * Manages user preferences for AI Studio including mode, batch size,
 * enabled categories, and dimension toggles.
 */

import { useCallback, useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import {
  IUseAIStudioPreferencesReturn,
  IAIStudioPreferencesInput,
  IAIStudioPreferences,
  getDefaultPreferencesInput,
  SceneCategory,
} from '@/@types/ai-studio';

/**
 * Hook for managing AI Studio preferences
 *
 * Provides:
 * - Current preferences (or null if not set)
 * - Default preferences for new users
 * - Loading and saving states
 * - Error handling
 * - Update and reset actions
 */
export function useAIStudioPreferences(): IUseAIStudioPreferencesReturn {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Query current preferences
  const rawPreferences = useQuery(api.aiStudio.preferences.getPreferences, {});

  // Mutations
  const savePreferencesMutation = useMutation(api.aiStudio.preferences.savePreferences);
  const resetPreferencesMutation = useMutation(api.aiStudio.preferences.resetPreferences);

  // Get default preferences
  const defaultPreferences = getDefaultPreferencesInput();

  /**
   * Transform raw Convex preferences to interface format
   * Maps defaultMode -> mode and defaultBatchSize -> batchSize
   */
  const transformPreferences = (raw: NonNullable<typeof rawPreferences>): IAIStudioPreferences => {
    return {
      _id: raw._id,
      _creationTime: raw._creationTime,
      userId: raw.userId,
      mode: raw.defaultMode,
      batchSize: raw.defaultBatchSize as IAIStudioPreferences['batchSize'],
      enabledCategories: raw.enabledCategories as SceneCategory[],
      enabledDimensions: raw.enabledDimensions,
      createdAt: raw._creationTime,
      updatedAt: raw.updatedAt,
    };
  };

  // Transform preferences if available
  const preferences = rawPreferences ? transformPreferences(rawPreferences) : null;

  /**
   * Update preferences with partial input
   * Merges provided values with existing preferences or defaults
   */
  const updatePreferences = useCallback(
    async (
      input: Partial<IAIStudioPreferencesInput>
    ): Promise<{ success: boolean; error?: string }> => {
      setIsSaving(true);
      setError(null);

      try {
        // Merge with current preferences or defaults
        const currentValues = preferences ?? {
          mode: defaultPreferences.mode,
          batchSize: defaultPreferences.batchSize,
          enabledCategories: defaultPreferences.enabledCategories,
          enabledDimensions: defaultPreferences.enabledDimensions,
        };

        const mergedInput: IAIStudioPreferencesInput = {
          mode: input.mode ?? currentValues.mode,
          batchSize: input.batchSize ?? currentValues.batchSize,
          enabledCategories: input.enabledCategories ?? currentValues.enabledCategories,
          enabledDimensions: input.enabledDimensions ?? currentValues.enabledDimensions,
        };

        await savePreferencesMutation({
          defaultMode: mergedInput.mode,
          defaultBatchSize: mergedInput.batchSize,
          enabledCategories: mergedInput.enabledCategories,
          enabledDimensions: mergedInput.enabledDimensions,
        });

        return { success: true };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to save preferences';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsSaving(false);
      }
    },
    [preferences, defaultPreferences, savePreferencesMutation]
  );

  /**
   * Reset preferences to default values
   */
  const resetToDefaults = useCallback(async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    setIsSaving(true);
    setError(null);

    try {
      await resetPreferencesMutation({});
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset preferences';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsSaving(false);
    }
  }, [resetPreferencesMutation]);

  /**
   * Clear current error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    preferences,
    defaultPreferences,
    isLoading: rawPreferences === undefined,
    isSaving,
    error,
    updatePreferences,
    resetToDefaults,
    clearError,
  };
}
